import React, { useState, useEffect, useRef } from "react";
import "../../App.css";
import { useApp } from '../../core/context';
import { verifyPassword, syncAuthPassword } from '../../api';
import { Auth } from '../../core/auth';
import { OTPService } from '../../core/otp-service';
import { Icon } from '../../components/Icon';

function AdminChangePasswordForm({ user, onClose, showToast, forced, onSuccess }) {
  const [form, setForm] = useState({ current: "", newPwd: "", confirm: "" });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!forced && !form.current) { showToast("Enter current password", "error"); return; }
    if (forced && !form.current) { showToast("Enter your temporary password", "error"); return; }
    if (form.newPwd.length < 6) { showToast("New password must be 6+ characters", "error"); return; }
    if (form.newPwd !== form.confirm) { showToast("Passwords do not match", "error"); return; }
    setBusy(true);
    const email = user?.email || `${user?.phone}@fb.local`;
    const verified = await verifyPassword(email, form.current);
    if (!verified) {
      showToast(forced ? "Temporary password is incorrect" : "Current password is incorrect", "error");
      setBusy(false);
      return;
    }
    const res = await syncAuthPassword(user.id, form.newPwd);
    if (!res.ok) { showToast("Couldn't change password: " + (res.error || "please try again"), "error"); setBusy(false); return; }
    showToast(forced ? "Password updated. Welcome!" : "Password changed. Please log in again with your new password.", "success");
    if (onSuccess) {
      onSuccess();
      return;
    }
    onClose();
    setTimeout(() => { try { localStorage.removeItem("fb_session"); localStorage.removeItem("fb_user"); } catch (e) {} window.location.reload(); }, 1400);
  };

  return (
    <>
      {forced && (
        <div className="info-box" style={{ background: "#FFF7ED", border: "1px solid #FED7AA", marginBottom: 16 }}>
          <div style={{ fontSize: 12, color: "#C2410C" }}>
            For security, you must set a new password before using the portal. Enter the temporary password you were given, then choose a new one.
          </div>
        </div>
      )}
      <div className="form-grid">
        <div className="f-group"><label className="f-label">{forced ? "Temporary / current password" : "Current Password"}</label><input className="f-input" type="password" value={form.current} onChange={(e) => set("current", e.target.value)} autoFocus={forced} /></div>
        <div className="f-group"><label className="f-label">New Password</label><input className="f-input" type="password" placeholder="Minimum 6 characters" value={form.newPwd} onChange={(e) => set("newPwd", e.target.value)} /></div>
        <div className="f-group"><label className="f-label">Confirm New Password</label><input className="f-input" type="password" value={form.confirm} onChange={(e) => set("confirm", e.target.value)} onKeyDown={(e) => e.key === "Enter" && save()} /></div>
      </div>
      <div className="modal-foot">
        {!forced && <button className="btn" onClick={onClose}>Cancel</button>}
        <button className="btn btn-primary" onClick={save} disabled={busy}>{busy ? "Changing…" : forced ? "Set new password" : "Change Password"}</button>
      </div>
    </>
  );
}

function OtpChangePasswordForm({ user, onClose, showToast }) {
  const email = user?.email || "";
  const [otpSent, setOtpSent] = useState(false);
  const [step, setStep] = useState(1);
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [resetToken, setResetToken] = useState(null);
  const [newPwd, setNewPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [countdown, setCountdown] = useState(0);
  const otpRefs = [useRef(), useRef(), useRef(), useRef(), useRef(), useRef()];

  useEffect(() => {
    if (countdown > 0) {
      const t = setTimeout(() => setCountdown((c) => c - 1), 1000);
      return () => clearTimeout(t);
    }
  }, [countdown]);

  useEffect(() => () => OTPService.resetRecaptcha(), []);

  const sendOtp = async () => {
    setError("");
    if (!email.includes("@")) { setError("No email on your account"); return false; }
    setBusy(true);
    const result = await OTPService.sendEmail("change_password", email);
    setBusy(false);
    if (!result.success) { setError(result.error); return false; }
    setCountdown(30);
    setOtp(["", "", "", "", "", ""]);
    if (result.demo) showToast(`Demo OTP: ${result.otp}`, "info");
    else showToast(`OTP sent to ${email}`, "success");
    setOtpSent(true);
    return true;
  };

  const verifyOtp = async () => {
    setError("");
    const entered = otp.join("");
    if (entered.length < 6) { setError("Enter all 6 digits"); return; }
    setBusy(true);
    const result = await OTPService.verifyEmail("change_password", email, entered);
    setBusy(false);
    if (!result.success) { setError(result.error); return; }
    setResetToken(result.token);
    setStep(2);
  };

  const save = async () => {
    setError("");
    if (newPwd.length < 6) { setError("Password must be at least 6 characters"); return; }
    if (newPwd !== confirm) { setError("Passwords do not match"); return; }
    setBusy(true);
    const result = await Auth.changePassword(resetToken, newPwd);
    setBusy(false);
    if (result.error) { setError(result.error); return; }
    showToast("Password changed. Please log in again.", "success");
    onClose();
    setTimeout(() => { try { localStorage.removeItem("fb_session"); localStorage.removeItem("fb_user"); } catch (e) {} window.location.reload(); }, 1400);
  };

  const handleOtpChange = (i, val) => {
    if (!/^\d*$/.test(val)) return;
    const next = [...otp];
    next[i] = val.slice(-1);
    setOtp(next);
    if (val && i < 5) otpRefs[i + 1].current?.focus();
    if (!val && i > 0) otpRefs[i - 1].current?.focus();
  };

  if (step === 2) {
    return (
      <>
        <div className="form-grid">
          <div className="f-group"><label className="f-label">New Password</label><input className="f-input" type="password" placeholder="Minimum 6 characters" value={newPwd} onChange={(e) => setNewPwd(e.target.value)} /></div>
          <div className="f-group"><label className="f-label">Confirm New Password</label><input className="f-input" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} onKeyDown={(e) => e.key === "Enter" && save()} /></div>
        </div>
        {error && <div style={{ fontSize: 12, color: "var(--red)", marginBottom: 12 }}>{error}</div>}
        <div className="modal-foot"><button className="btn" onClick={onClose}>Cancel</button><button className="btn btn-primary" onClick={save} disabled={busy}>{busy ? "Saving…" : "Change Password"}</button></div>
      </>
    );
  }

  return (
    <>
      <div style={{ fontSize: 12, color: "var(--muted)", marginBottom: 12 }}>
        {otpSent
          ? <>Enter the 6-digit code sent to <strong>{email}</strong></>
          : <>We'll send a verification code to <strong>{email}</strong></>}
      </div>
      {!otpSent && (
        <button className="btn btn-primary" style={{ width: "100%", marginBottom: 14 }} onClick={sendOtp} disabled={busy || countdown > 0}>
          {countdown > 0 ? `Resend OTP in ${countdown}s` : busy ? "Sending…" : "Send OTP to Email"}
        </button>
      )}
      {otpSent && (
        <>
          <div className="otp-grid">
            {otp.map((d, i) => (
              <input key={i} ref={otpRefs[i]} className="f-input otp-box" inputMode="numeric" maxLength={1} value={d} onChange={(e) => handleOtpChange(i, e.target.value)} />
            ))}
          </div>
          <button className="btn btn-sm" style={{ width: "100%", margin: "10px 0" }} disabled={countdown > 0 || busy} onClick={sendOtp}>
            {countdown > 0 ? `Resend in ${countdown}s` : "Resend OTP"}
          </button>
        </>
      )}
      {error && <div style={{ fontSize: 12, color: "var(--red)", marginBottom: 12 }}>{error}</div>}
      <div className="modal-foot">
        <button className="btn" onClick={onClose}>Cancel</button>
        {otpSent && <button className="btn btn-primary" onClick={verifyOtp} disabled={busy}>{busy ? "Verifying…" : "Verify & Continue"}</button>}
      </div>
    </>
  );
}

function ChangePasswordModal({ onClose, showToast, forced = false, onSuccess }) {
  const { user } = useApp();
  const useOtp = !forced && (user?.role === "client" || user?.role === "employee" || user?.role === "manager" || user?.role === "sales");

  return (
    <div className="modal-box" style={{ maxWidth: 420 }}>
      <div className="modal-head">
        <div className="modal-title">{forced ? "Set your password" : "Change Password"}</div>
        {!forced && <button className="modal-close" onClick={onClose}><Icon name="close" size={14} /></button>}
      </div>
      <div className="modal-body">
        {!forced && (
          <div className="info-box" style={{ background: "#EFF6FF", border: "1px solid #BFDBFE", marginBottom: 16 }}>
            <span><Icon name="lock" size={16} /></span>
            <div style={{ fontSize: 12, color: "var(--blue)" }}>
              Changing password for <strong>{user?.name}</strong> ({user?.email})
              {useOtp && <div style={{ marginTop: 4 }}>Email OTP verification required (accounts are created by admin).</div>}
            </div>
          </div>
        )}
        {forced || !useOtp
          ? <AdminChangePasswordForm user={user} onClose={onClose} showToast={showToast} forced={forced} onSuccess={onSuccess} />
          : <OtpChangePasswordForm user={user} onClose={onClose} showToast={showToast} />}
      </div>
    </div>
  );
}

export { ChangePasswordModal };
